import React, {useMemo} from 'react';
import {Icon, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {SortDir, SortOption} from '@/types/sorting';
import Dropdown, {DropdownOption} from './Dropdown';

const ICON_STYLE = {width: '18px', height: '18px'};

type SortByDropdownProps = {
  options: SortOption[];
  sortBy?: string;
  sortDir?: SortDir;
  onChange: (id: string, dir: SortDir) => void;
  className?: string;
  onOpen?: (event: React.MouseEvent<HTMLElement>) => void;
};

function SortByDropdown({
  options,
  sortBy,
  sortDir,
  onChange,
  className,
  onOpen,
}: SortByDropdownProps) {
  const sortDirIcon = useMemo(
    () =>
      sortDir ? (
        <Icon
          size="medium"
          appearance="neutral"
          name={
            sortDir === 'asc'
              ? 'Navigation-Sort-Ascending'
              : 'Navigation-Sort-Descending'
          }
          color={designSystemToken('semantic.fg.inverse-secondary')}
        />
      ) : null,
    [sortDir]
  );

  const dropdownOptions: DropdownOption[] = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        endIcon: option.id === sortBy ? sortDirIcon : null,
      })),
    [options, sortBy, sortDirIcon]
  );

  const label =
    options.find((o) => o.id === sortBy)?.label ?? translate('Sort by');

  return (
    <Dropdown
      className={className}
      label={label}
      title={translate('Sort by')}
      options={dropdownOptions}
      selectedIds={sortBy ? [sortBy] : []}
      onOpen={onOpen}
      icon={
        <Icon
          appearance="neutral"
          name="Navigation-Arrow-Down-Up"
          color={designSystemToken('semantic.fg.inverse-secondary')}
          style={ICON_STYLE}
        />
      }
      onChange={(option) => {
        const newDir: SortDir =
          option.id === sortBy && sortDir === 'desc' ? 'asc' : 'desc';
        onChange(option.id, newDir);
      }}
    />
  );
}

export default SortByDropdown;
